import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { get, post, patch, del } from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { Skeleton } from "@/components/common/EmptyState";
import { SearchInput } from "@/components/common/SearchInput";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { dateFmt } from "@/lib/format";
import { Flag, Pin, Plus, Trash2 } from "lucide-react";

const PRIO = { high: "text-red-500", medium: "text-amber-500", low: "text-blue-500", none: "text-muted-foreground/40" };
const PRIORITIES = ["none", "low", "medium", "high"];
const COLORS = ["#FEF3C7", "#DBEAFE", "#DCFCE7", "#EDE9FE", "#FEE2E2"];

export default function Todo() {
  const qc = useQueryClient();
  const [newTask, setNewTask] = useState("");
  const [taskSearch, setTaskSearch] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [noteSearch, setNoteSearch] = useState("");
  const { data, isLoading } = useQuery({ queryKey: ["todo"], queryFn: () => get("/todo") });

  const inval = () => qc.invalidateQueries({ queryKey: ["todo"] });
  const addTask = useMutation({ mutationFn: (body) => post("/todo/tasks", body), onSuccess: inval });
  const updTask = useMutation({ mutationFn: ({ id, body }) => patch(`/todo/tasks/${id}`, body), onSuccess: inval });
  const delTask = useMutation({ mutationFn: (id) => del(`/todo/tasks/${id}`), onSuccess: inval });
  const addNote = useMutation({ mutationFn: (body) => post("/todo/notes", body), onSuccess: inval });
  const updNote = useMutation({ mutationFn: ({ id, body }) => patch(`/todo/notes/${id}`, body), onSuccess: inval });

  const filteredTasks = useMemo(() => {
    return (data?.tasks || []).filter((t) => {
      if (taskSearch && !t.text?.toLowerCase().includes(taskSearch.toLowerCase())) return false;
      if (priorityFilter !== "all" && (t.priority || "none") !== priorityFilter) return false;
      return true;
    });
  }, [data, taskSearch, priorityFilter]);
  const filteredNotes = useMemo(() => {
    return [...(data?.notes || [])]
      .filter((n) => !noteSearch || n.body?.toLowerCase().includes(noteSearch.toLowerCase()))
      .sort((a, b) => b.pinned - a.pinned);
  }, [data, noteSearch]);

  if (isLoading || !data) return <Skeleton className="h-96" />;
  const cyclePrio = (p) => ({ none: "low", low: "medium", medium: "high", high: "none" }[p || "none"]);

  return (
    <div>
      <PageHeader title="To-Do & Notes" subtitle="Capture your day, quickly." />
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="rounded-xl border border-border bg-card p-4" data-testid="todo-list">
          <h3 className="mb-3 font-display text-sm font-semibold">My Tasks</h3>
          {data.tasks.length > 0 && (
            <div className="mb-3 flex items-center gap-2">
              <SearchInput testid="task-search" placeholder="Search tasks" value={taskSearch} onChange={setTaskSearch} className="flex-1" />
              <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                <SelectTrigger className="w-32" data-testid="task-priority-filter"><SelectValue placeholder="All" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  {PRIORITIES.map((p) => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-1">
            {data.tasks.length > 0 && !filteredTasks.length && (
              <p className="px-2 py-3 text-sm text-muted-foreground">No tasks match your search or filter.</p>
            )}
            {filteredTasks.map((t) => (
              <div key={t.id} data-testid={`task-${t.id}`} className="group flex items-center gap-2.5 rounded-lg px-2 py-1.5 hover:bg-muted/50">
                <Checkbox checked={t.done} onCheckedChange={(v) => updTask.mutate({ id: t.id, body: { done: !!v } })} data-testid={`task-check-${t.id}`} />
                <button onClick={() => updTask.mutate({ id: t.id, body: { priority: cyclePrio(t.priority) } })}><Flag className={cn("h-3.5 w-3.5", PRIO[t.priority || "none"])} /></button>
                <span className={cn("flex-1 text-sm", t.done && "text-muted-foreground line-through")}>{t.text}</span>
                {t.due && <span className="text-xs text-muted-foreground">{dateFmt(t.due)}</span>}
                <button onClick={() => delTask.mutate(t.id)} className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-red-500"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            ))}
          </div>
          <div className="mt-2 flex items-center gap-2 border-t border-border pt-2">
            <Plus className="h-4 w-4 text-muted-foreground" />
            <input data-testid="new-task-input" value={newTask} onChange={(e) => setNewTask(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && newTask.trim()) { addTask.mutate({ list_id: "list-default", text: newTask }); setNewTask(""); } }}
              placeholder="Add a task and press Enter" className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground" />
          </div>
        </div>

        <div>
          <div className="mb-3 flex items-center justify-between">
            <h3 className="font-display text-sm font-semibold">Sticky Notes</h3>
            <button data-testid="add-note" onClick={() => addNote.mutate({ body: "New note", color: COLORS[Math.floor(Math.random() * COLORS.length)] })} className="flex items-center gap-1 text-sm text-primary hover:underline"><Plus className="h-3.5 w-3.5" />Add</button>
          </div>
          {data.notes.length > 0 && (
            <div className="mb-3"><SearchInput testid="note-search" placeholder="Search notes" value={noteSearch} onChange={setNoteSearch} className="w-full" /></div>
          )}
          <div className="grid grid-cols-2 gap-3">
            {data.notes.length > 0 && !filteredNotes.length && (
              <p className="col-span-2 text-sm text-muted-foreground">No notes match your search.</p>
            )}
            {filteredNotes.map((n) => (
              <div key={n.id} data-testid={`note-${n.id}`} className="rounded-xl p-3 shadow-sm dark:text-slate-900" style={{ background: n.color }}>
                <div className="flex justify-end"><button onClick={() => updNote.mutate({ id: n.id, body: { pinned: !n.pinned } })}><Pin className={cn("h-3.5 w-3.5", n.pinned ? "fill-slate-900" : "text-slate-500")} /></button></div>
                <textarea defaultValue={n.body} onBlur={(e) => updNote.mutate({ id: n.id, body: { body: e.target.value } })}
                  className="w-full resize-none bg-transparent text-sm outline-none" rows={3} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
